import type {
  adminAuditList,
  adminHealthList,
  adminIntegrations,
  adminOrganizationDetail,
  adminOrganizationList,
  adminOverview,
  adminUsage,
  adminUserDetail,
  adminUserList,
  adminUserSessionsRevoked,
  alertList,
  alertObject,
  alertRuleList,
  alertRuleObject,
  audienceSettings,
  auditList,
  automationList,
  automationRunList,
  automationRunObject,
  automationVersionList,
  broadcastAudienceRequest,
  broadcastAudienceResponse,
  broadcastIdResponse,
  broadcastList,
  broadcastObject,
  broadcastRecipientList,
  broadcastTestSendRequest,
  cloudflareConnectionObject,
  connectCloudflareRequest,
  consentList,
  consoleAutomationObject,
  consoleCreateWebhookRequest,
  consoleDomain,
  consoleEmailDetail,
  consoleTestEmailOptions,
  consoleTopicList,
  contactActivityList,
  contactList,
  contactObject,
  contactPropertyObject,
  createAutomationRequest,
  createBroadcastRequest,
  createContactPropertyRequest,
  createContactRequest,
  createDomainRequest,
  createEnvironmentRequest,
  createForwardAddressRequest,
  createImportRequest,
  createInboundRouteRequest,
  createKeyRequest,
  createMailboxRequest,
  createMailboxResponse,
  createOauthClientRequest,
  createOrganizationRequest,
  createProjectRequest,
  createSegmentRequest,
  createSmtpCredentialRequest,
  createSuppressionRequest,
  createTemplateRequest,
  createTopicRequest,
  deleted,
  dnsPlan,
  domainAutoconfigure,
  domainList,
  emailIdResponse,
  emailList,
  environmentList,
  environmentObject,
  exportRequest,
  exportResponse,
  fixDnsRequest,
  forwardAddressList,
  forwardAddressObject,
  importList,
  importObject,
  inboundList,
  inboundObject,
  inboundRouteList,
  inboundRouteObject,
  invitationList,
  invitationObject,
  inviteMemberRequest,
  keyList,
  keyObject,
  listOf,
  logList,
  mailboxList,
  mailboxObject,
  memberList,
  memberObject,
  metricsResponse,
  oauthClientList,
  oauthClientObject,
  onboardingObject,
  organizationList,
  organizationObject,
  platformSettingsObject,
  previewTemplateRequest,
  previewTemplateResponse,
  projectObject,
  receivingDomainList,
  receivingDomainObject,
  replayRequest,
  replayResponse,
  replySupportTicketRequest,
  revokedSession,
  rotateWebhookSecretResponse,
  segmentCountRequest,
  segmentCountResponse,
  segmentList,
  segmentMembersAdded,
  segmentMembersRemoved,
  segmentMembersRequest,
  segmentObject,
  sendBroadcastRequest,
  sendEmailRequest,
  sendEmailResponse,
  sessionList,
  sessionObject,
  sessionResponseObject,
  signInEmailStartRequest,
  signInEmailStartResponse,
  signInEmailVerifyRequest,
  signOutResponse,
  smtpCredentialList,
  smtpCredentialObject,
  statusObject,
  supportTicketDetail,
  supportTicketList,
  suppressionList,
  suppressionObject,
  templateList,
  templateObject,
  templateVersionObject,
  testWebhookRequest,
  testWebhookResponse,
  topicObject,
  updateAdminOrganizationRequest,
  updateAdminUserRequest,
  updateAlertRequest,
  updateAlertRuleRequest,
  updateAudienceSettingsRequest,
  updateAutomationRequest,
  updateBroadcastRequest,
  updateContactRequest,
  updateDomainRequest,
  updateInboundRouteRequest,
  updateMemberRequest,
  updateOnboardingRequest,
  updateOrganizationRequest,
  updatePlatformSettingsRequest,
  updateProjectRequest,
  updateSegmentRequest,
  updateSupportTicketRequest,
  updateTemplateRequest,
  updateTopicRequest,
  updateWebhookRequest,
  webhookDeliveryList,
  webhookList,
  webhookObject,
} from "@minket/core";
import type { z } from "zod";
import type { Method } from "./api";

/**
 * Every console API endpoint the console calls, by method and path pattern, written exactly
 * as the route table in apps/api/src/routes/console/table.ts declares it (a frozen contract),
 * with the type of its success body and, for mutations, its JSON body.
 * `endpoints.test.ts` reads that table and fails when an entry here is missing from it.
 *
 * The types are `z.infer` of `@minket/core` schemas through type-only imports, so neither zod
 * nor a schema reaches the bundle (they would cost every route about 33 KB gzipped of its
 * 150 KB budget). The console trusts the API's shapes at runtime: both are built from the
 * same core schemas and deployed together, and the e2e fixtures are checked against those
 * schemas in `e2e/fixtures.test.ts`. Where core has no schema for a response yet the type is
 * `unknown`, with a note naming the step that adds it.
 *
 * Add an endpoint here, in its area's block, before calling it; then call it with `apiQuery`
 * (GET) or `apiRequest` (anything) from lib/query.ts.
 */

/** A core schema's wire shape, as the API sends it (snake_case JSON). Type-only. */
export type Wire<S extends z.ZodType> = z.output<S>;
/** What a request body may hold before core's defaults apply. Type-only. */
export type WireInput<S extends z.ZodType> = z.input<S>;
/** Core's list envelope around a core object, for lists core has no named schema for yet. */
export type WireList<S extends z.ZodType> = z.output<ReturnType<typeof listOf<S>>>;

export interface Endpoint<
  M extends Method = Method,
  P extends string = string,
  Res = unknown,
  Body = undefined,
> {
  readonly method: M;
  readonly path: P;
  /** Type-level only, never set: the success body. */
  readonly response?: Res;
  /** Type-level only, never set: the JSON request body (`undefined` for none). */
  readonly requestBody?: Body;
}

/** Any endpoint, whatever it returns or takes. */
export type AnyEndpoint = Endpoint<Method, string, unknown, unknown>;

/** `GET /path`, then `.returns<Wire<typeof schema>>()`. */
class Declaration<M extends Method, P extends string, Body = undefined> {
  constructor(
    readonly method: M,
    readonly path: P,
  ) {}

  /** The JSON body the endpoint takes. */
  takes<B>(): Declaration<M, P, B> {
    return new Declaration<M, P, B>(this.method, this.path);
  }

  /** The success body (`null` for an empty 204). Ends the declaration. */
  returns<Res>(): Endpoint<M, P, Res, Body> {
    return { method: this.method, path: this.path };
  }
}

const declare =
  <M extends Method>(method: M) =>
  <const P extends string>(path: P) =>
    new Declaration(method, path);

/*
 * One declarer per method, so a build step adding its first mutation only adds entries:
 *   createKey: post("/projects/:projectId/keys")
 *     .takes<WireInput<typeof createKeyRequest>>()
 *     .returns<Wire<typeof keyObject>>(),
 */
const get = declare("GET");
const patch = declare("PATCH");
const post = declare("POST");
const del = declare("DELETE");

/** The body of a delete that answers with the id it removed: core's `deleted()`. */
type Deleted<O extends string> = Wire<ReturnType<typeof deleted<O>>>;

export const endpoints = {
  // Session, sign-in and the caller's sessions (B12)
  session: get("/session").returns<Wire<typeof sessionResponseObject>>(),
  signInStart: post("/auth/email/start")
    .takes<WireInput<typeof signInEmailStartRequest>>()
    .returns<Wire<typeof signInEmailStartResponse>>(),
  signInVerify: post("/auth/email/verify")
    .takes<WireInput<typeof signInEmailVerifyRequest>>()
    .returns<Wire<typeof sessionObject>>(),
  signOut: post("/auth/signout").returns<Wire<typeof signOutResponse>>(),
  sessions: get("/account/sessions").returns<Wire<typeof sessionList>>(),
  revokeSession: del("/account/sessions/:sessionId").returns<Wire<typeof revokedSession>>(),

  // Session and organization
  organization: get("/orgs/:orgId").returns<Wire<typeof organizationObject>>(),
  members: get("/orgs/:orgId/members").returns<Wire<typeof memberList>>(),
  /** Core has no `projectList` yet: its list envelope around its project object. */
  projects: get("/orgs/:orgId/projects").returns<WireList<typeof projectObject>>(),
  audit: get("/orgs/:orgId/audit").returns<Wire<typeof auditList>>(),

  // Settings: Cloudflare, SMTP credentials (B5)
  cloudflare: get("/orgs/:orgId/cloudflare").returns<Wire<typeof cloudflareConnectionObject>>(),
  connectCloudflare: post("/orgs/:orgId/cloudflare")
    .takes<WireInput<typeof connectCloudflareRequest>>()
    .returns<Wire<typeof cloudflareConnectionObject>>(),
  disconnectCloudflare:
    del("/orgs/:orgId/cloudflare").returns<Wire<typeof cloudflareConnectionObject>>(),
  smtp: get("/orgs/:orgId/smtp").returns<Wire<typeof smtpCredentialList>>(),
  createSmtp: post("/orgs/:orgId/smtp")
    .takes<WireInput<typeof createSmtpCredentialRequest>>()
    .returns<Wire<typeof smtpCredentialObject>>(),
  deleteSmtp: del("/orgs/:orgId/smtp/:credentialId").returns<Wire<typeof smtpCredentialObject>>(),

  // Operations (B10)
  alerts: get("/orgs/:orgId/alerts").returns<Wire<typeof alertList>>(),
  updateAlert: patch("/orgs/:orgId/alerts/:alertId")
    .takes<WireInput<typeof updateAlertRequest>>()
    .returns<Wire<typeof alertObject>>(),
  alertRules: get("/orgs/:orgId/alerts/rules").returns<Wire<typeof alertRuleList>>(),
  updateAlertRule: patch("/orgs/:orgId/alerts/rules/:ruleId")
    .takes<WireInput<typeof updateAlertRuleRequest>>()
    .returns<Wire<typeof alertRuleObject>>(),
  onboarding: get("/orgs/:orgId/onboarding").returns<Wire<typeof onboardingObject>>(),
  updateOnboarding: patch("/orgs/:orgId/onboarding")
    .takes<WireInput<typeof updateOnboardingRequest>>()
    .returns<Wire<typeof onboardingObject>>(),
  metrics: get("/orgs/:orgId/metrics").returns<Wire<typeof metricsResponse>>(),
  status: get("/orgs/:orgId/status").returns<Wire<typeof statusObject>>(),

  // Tenancy (B2): organizations, members, invitations, projects, environments, keys
  organizations: get("/orgs").returns<Wire<typeof organizationList>>(),
  createOrganization: post("/orgs")
    .takes<WireInput<typeof createOrganizationRequest>>()
    .returns<Wire<typeof organizationObject>>(),
  updateOrganization: patch("/orgs/:orgId")
    .takes<WireInput<typeof updateOrganizationRequest>>()
    .returns<Wire<typeof organizationObject>>(),
  deleteOrganization: del("/orgs/:orgId").returns<Deleted<"organization">>(),
  /** A member when the address has signed in before, otherwise an invitation. */
  addMember: post("/orgs/:orgId/members")
    .takes<WireInput<typeof inviteMemberRequest>>()
    .returns<Wire<typeof memberObject> | Wire<typeof invitationObject>>(),
  updateMember: patch("/orgs/:orgId/members/:memberId")
    .takes<WireInput<typeof updateMemberRequest>>()
    .returns<Wire<typeof memberObject>>(),
  removeMember: del("/orgs/:orgId/members/:memberId").returns<Deleted<"member">>(),
  invitations: get("/orgs/:orgId/invitations").returns<Wire<typeof invitationList>>(),
  revokeInvitation: del("/orgs/:orgId/invitations/:invitationId").returns<Deleted<"invitation">>(),
  createProject: post("/orgs/:orgId/projects")
    .takes<WireInput<typeof createProjectRequest>>()
    .returns<Wire<typeof projectObject>>(),
  updateProject: patch("/projects/:projectId")
    .takes<WireInput<typeof updateProjectRequest>>()
    .returns<Wire<typeof projectObject>>(),
  deleteProject: del("/projects/:projectId").returns<Deleted<"project">>(),
  /** S-08: a new link signing secret; the replaced one keeps verifying until the next rotation. */
  rotateSigningSecret: post("/projects/:projectId/signing/rotate").returns<
    Wire<typeof projectObject>
  >(),
  environments: get("/projects/:projectId/environments").returns<Wire<typeof environmentList>>(),
  createEnvironment: post("/projects/:projectId/environments")
    .takes<WireInput<typeof createEnvironmentRequest>>()
    .returns<Wire<typeof environmentObject>>(),
  keys: get("/projects/:projectId/keys").returns<Wire<typeof keyList>>(),
  /** `token` is in this response only. */
  createKey: post("/projects/:projectId/keys")
    .takes<WireInput<typeof createKeyRequest>>()
    .returns<Wire<typeof keyObject>>(),
  revokeKey: del("/projects/:projectId/keys/:keyId").returns<
    Pick<Wire<typeof keyObject>, "id" | "object" | "revoked_at">
  >(),

  // Sending (B3)
  emails: get("/projects/:projectId/emails").returns<Wire<typeof emailList>>(),
  /** The email object plus what only the console shows: see `EmailDetail`. */
  email: get("/projects/:projectId/emails/:emailId").returns<EmailDetail>(),
  cancelEmail: post("/projects/:projectId/emails/:emailId/cancel").returns<
    Wire<typeof emailIdResponse>
  >(),
  sendTestEmail: post("/projects/:projectId/emails/test")
    .takes<TestEmailRequest>()
    .returns<Wire<typeof sendEmailResponse>>(),
  logs: get("/projects/:projectId/logs").returns<Wire<typeof logList>>(),
  suppressions: get("/projects/:projectId/suppressions").returns<Wire<typeof suppressionList>>(),
  createSuppression: post("/projects/:projectId/suppressions")
    .takes<WireInput<typeof createSuppressionRequest>>()
    .returns<Wire<typeof suppressionObject>>(),
  deleteSuppression: del("/projects/:projectId/suppressions/:suppressionId").returns<
    Deleted<"suppression">
  >(),
  /** Test-mode mail, in the emails list's shape. */
  sandbox: get("/projects/:projectId/sandbox").returns<Wire<typeof emailList>>(),
  templates: get("/projects/:projectId/templates").returns<Wire<typeof templateList>>(),
  template: get("/projects/:projectId/templates/:templateId").returns<
    Wire<typeof templateObject>
  >(),

  // Hosted templates (B3 templates)
  createTemplate: post("/projects/:projectId/templates")
    .takes<WireInput<typeof createTemplateRequest>>()
    .returns<Wire<typeof templateObject>>(),
  updateTemplate: patch("/projects/:projectId/templates/:templateId")
    .takes<WireInput<typeof updateTemplateRequest>>()
    .returns<Wire<typeof templateObject>>(),
  deleteTemplate: del("/projects/:projectId/templates/:templateId").returns<
    Wire<ReturnType<typeof deleted<"template">>>
  >(),
  publishTemplate: post("/projects/:projectId/templates/:templateId/publish").returns<
    Wire<typeof templateObject>
  >(),
  duplicateTemplate: post("/projects/:projectId/templates/:templateId/duplicate").returns<
    Wire<typeof templateObject>
  >(),
  previewTemplate: post("/projects/:projectId/templates/:templateId/preview")
    .takes<WireInput<typeof previewTemplateRequest>>()
    .returns<Wire<typeof previewTemplateResponse>>(),
  templateVersions: get("/projects/:projectId/templates/:templateId/versions").returns<
    WireList<typeof templateVersionObject>
  >(),

  // Webhooks (B4)
  webhooks: get("/projects/:projectId/webhooks").returns<Wire<typeof webhookList>>(),
  createWebhook: post("/projects/:projectId/webhooks")
    .takes<WireInput<typeof consoleCreateWebhookRequest>>()
    .returns<Wire<typeof webhookObject>>(),
  updateWebhook: patch("/projects/:projectId/webhooks/:webhookId")
    .takes<WireInput<typeof updateWebhookRequest>>()
    .returns<Wire<typeof webhookObject>>(),
  deleteWebhook: del("/projects/:projectId/webhooks/:webhookId").returns<
    Pick<Wire<typeof webhookObject>, "id" | "object">
  >(),
  rotateWebhook: post("/projects/:projectId/webhooks/:webhookId/rotate").returns<
    Wire<typeof rotateWebhookSecretResponse>
  >(),
  webhookDeliveries: get("/projects/:projectId/webhooks/:webhookId/deliveries").returns<
    Wire<typeof webhookDeliveryList>
  >(),
  replayWebhook: post("/projects/:projectId/webhooks/:webhookId/replay")
    .takes<WireInput<typeof replayRequest>>()
    .returns<Wire<typeof replayResponse>>(),
  testWebhook: post("/projects/:projectId/webhooks/:webhookId/test")
    .takes<WireInput<typeof testWebhookRequest>>()
    .returns<Wire<typeof testWebhookResponse>>(),

  // Domains (B5)
  domains: get("/projects/:projectId/domains").returns<Wire<typeof domainList>>(),
  domain: get("/projects/:projectId/domains/:domainId").returns<Wire<typeof consoleDomain>>(),
  createDomain: post("/projects/:projectId/domains")
    .takes<WireInput<typeof createDomainRequest>>()
    .returns<Wire<typeof consoleDomain>>(),
  updateDomain: patch("/projects/:projectId/domains/:domainId")
    .takes<WireInput<typeof updateDomainRequest>>()
    .returns<Wire<typeof consoleDomain>>(),
  deleteDomain: del("/projects/:projectId/domains/:domainId").returns<Deleted<"domain">>(),
  verifyDomain: post("/projects/:projectId/domains/:domainId/verify").returns<
    Wire<typeof consoleDomain>
  >(),
  fixDns: post("/projects/:projectId/domains/:domainId/dns")
    .takes<WireInput<typeof fixDnsRequest>>()
    .returns<Wire<typeof dnsPlan>>(),
  /** How the domain's records get written: Fix DNS, the DNS provider's page, or by hand (D33). */
  domainAutoconfigure: get("/projects/:projectId/domains/:domainId/autoconfigure").returns<
    Wire<typeof domainAutoconfigure>
  >(),

  // Inbound (B6)
  inbound: get("/projects/:projectId/inbound").returns<Wire<typeof inboundList>>(),
  inboundMessage: get("/projects/:projectId/inbound/:inboundId").returns<
    Wire<typeof inboundObject>
  >(),
  inboundRoutes: get("/projects/:projectId/inbound/routes").returns<
    Wire<typeof inboundRouteList>
  >(),
  createInboundRoute: post("/projects/:projectId/inbound/routes")
    .takes<WireInput<typeof createInboundRouteRequest>>()
    .returns<Wire<typeof inboundRouteObject>>(),
  updateInboundRoute: patch("/projects/:projectId/inbound/routes/:routeId")
    .takes<WireInput<typeof updateInboundRouteRequest>>()
    .returns<Wire<typeof inboundRouteObject>>(),
  deleteInboundRoute: del("/projects/:projectId/inbound/routes/:routeId").returns<
    Pick<Wire<typeof inboundRouteObject>, "id" | "object">
  >(),
  forwarding: get("/projects/:projectId/inbound/forwarding").returns<
    Wire<typeof forwardAddressList>
  >(),
  addForwarding: post("/projects/:projectId/inbound/forwarding")
    .takes<WireInput<typeof createForwardAddressRequest>>()
    .returns<Wire<typeof forwardAddressObject>>(),
  removeForwarding: del("/projects/:projectId/inbound/forwarding/:addressId").returns<
    Deleted<"forward_address">
  >(),
  receiving: get("/projects/:projectId/inbound/receiving").returns<
    Wire<typeof receivingDomainList>
  >(),
  checkReceiving: post("/projects/:projectId/inbound/receiving/:domainId/check").returns<
    Wire<typeof receivingDomainObject>
  >(),

  // Mailboxes (B7)
  mailboxes: get("/projects/:projectId/mailboxes").returns<Wire<typeof mailboxList>>(),
  /** SMTP answers with the mailbox; Gmail and Microsoft with the consent URL to go to. */
  connectMailbox: post("/projects/:projectId/mailboxes")
    .takes<WireInput<typeof createMailboxRequest>>()
    .returns<Wire<typeof createMailboxResponse>>(),
  deleteMailbox: del("/projects/:projectId/mailboxes/:mailboxId").returns<
    Pick<Wire<typeof mailboxObject>, "id" | "object">
  >(),
  oauthClients: get("/projects/:projectId/oauth").returns<Wire<typeof oauthClientList>>(),
  saveOauthClient: post("/projects/:projectId/oauth")
    .takes<WireInput<typeof createOauthClientRequest>>()
    .returns<Wire<typeof oauthClientObject>>(),
  deleteOauthClient: del("/projects/:projectId/oauth/:clientId").returns<Deleted<"oauth_client">>(),

  // Audience (B8)
  contacts: get("/projects/:projectId/contacts").returns<Wire<typeof contactList>>(),
  createContact: post("/projects/:projectId/contacts")
    .takes<WireInput<typeof createContactRequest>>()
    .returns<Wire<typeof contactObject>>(),
  contact: get("/projects/:projectId/contacts/:contactId").returns<Wire<typeof contactObject>>(),
  updateContact: patch("/projects/:projectId/contacts/:contactId")
    .takes<WireInput<typeof updateContactRequest>>()
    .returns<Wire<typeof contactObject>>(),
  deleteContact: del("/projects/:projectId/contacts/:contactId").returns<Deleted<"contact">>(),
  contactActivity: get("/projects/:projectId/contacts/:contactId/activity").returns<
    Wire<typeof contactActivityList>
  >(),
  contactConsent: get("/projects/:projectId/contacts/:contactId/consent").returns<
    Wire<typeof consentList>
  >(),
  contactImports: get("/projects/:projectId/contacts/import").returns<Wire<typeof importList>>(),
  createContactImport: post("/projects/:projectId/contacts/import")
    .takes<WireInput<typeof createImportRequest>>()
    .returns<Wire<typeof importObject>>(),
  contactImport: get("/projects/:projectId/contacts/import/:importId").returns<
    Wire<typeof importObject>
  >(),
  exportContacts: post("/projects/:projectId/contacts/export")
    .takes<WireInput<typeof exportRequest>>()
    .returns<Wire<typeof exportResponse>>(),
  contactProperties: get("/projects/:projectId/contacts/properties").returns<
    WireList<typeof contactPropertyObject>
  >(),
  createContactProperty: post("/projects/:projectId/contacts/properties")
    .takes<WireInput<typeof createContactPropertyRequest>>()
    .returns<Wire<typeof contactPropertyObject>>(),
  deleteContactProperty: del("/projects/:projectId/contacts/properties/:propertyId").returns<
    Deleted<"contact_property">
  >(),
  audienceSettings: get("/projects/:projectId/contacts/settings").returns<
    Wire<typeof audienceSettings>
  >(),
  updateAudienceSettings: patch("/projects/:projectId/contacts/settings")
    .takes<WireInput<typeof updateAudienceSettingsRequest>>()
    .returns<Wire<typeof audienceSettings>>(),
  segments: get("/projects/:projectId/segments").returns<Wire<typeof segmentList>>(),
  createSegment: post("/projects/:projectId/segments")
    .takes<WireInput<typeof createSegmentRequest>>()
    .returns<Wire<typeof segmentObject>>(),
  countSegment: post("/projects/:projectId/segments/count")
    .takes<WireInput<typeof segmentCountRequest>>()
    .returns<Wire<typeof segmentCountResponse>>(),
  segment: get("/projects/:projectId/segments/:segmentId").returns<Wire<typeof segmentObject>>(),
  updateSegment: patch("/projects/:projectId/segments/:segmentId")
    .takes<WireInput<typeof updateSegmentRequest>>()
    .returns<Wire<typeof segmentObject>>(),
  deleteSegment: del("/projects/:projectId/segments/:segmentId").returns<Deleted<"segment">>(),
  segmentContacts: get("/projects/:projectId/segments/:segmentId/contacts").returns<
    Wire<typeof contactList>
  >(),
  addSegmentContacts: post("/projects/:projectId/segments/:segmentId/contacts")
    .takes<WireInput<typeof segmentMembersRequest>>()
    .returns<Wire<typeof segmentMembersAdded>>(),
  removeSegmentContacts: del("/projects/:projectId/segments/:segmentId/contacts")
    .takes<WireInput<typeof segmentMembersRequest>>()
    .returns<Wire<typeof segmentMembersRemoved>>(),
  /** The console's topic list carries each topic's subscriber count. */
  topics: get("/projects/:projectId/topics").returns<Wire<typeof consoleTopicList>>(),
  createTopic: post("/projects/:projectId/topics")
    .takes<WireInput<typeof createTopicRequest>>()
    .returns<Wire<typeof topicObject>>(),
  updateTopic: patch("/projects/:projectId/topics/:topicId")
    .takes<WireInput<typeof updateTopicRequest>>()
    .returns<Wire<typeof topicObject>>(),
  deleteTopic: del("/projects/:projectId/topics/:topicId").returns<Deleted<"topic">>(),

  // Broadcasts (B8)
  broadcasts: get("/projects/:projectId/broadcasts").returns<Wire<typeof broadcastList>>(),
  createBroadcast: post("/projects/:projectId/broadcasts")
    .takes<WireInput<typeof createBroadcastRequest>>()
    .returns<Wire<typeof broadcastObject>>(),
  broadcastAudience: post("/projects/:projectId/broadcasts/audience")
    .takes<WireInput<typeof broadcastAudienceRequest>>()
    .returns<Wire<typeof broadcastAudienceResponse>>(),
  broadcast: get("/projects/:projectId/broadcasts/:broadcastId").returns<
    Wire<typeof broadcastObject>
  >(),
  updateBroadcast: patch("/projects/:projectId/broadcasts/:broadcastId")
    .takes<WireInput<typeof updateBroadcastRequest>>()
    .returns<Wire<typeof broadcastObject>>(),
  deleteBroadcast: del("/projects/:projectId/broadcasts/:broadcastId").returns<
    Deleted<"broadcast">
  >(),
  /** Sends or schedules a draft, resumes a paused one; answers the broadcast. */
  sendBroadcast: post("/projects/:projectId/broadcasts/:broadcastId/send")
    .takes<WireInput<typeof sendBroadcastRequest>>()
    .returns<Wire<typeof broadcastObject>>(),
  cancelBroadcast: post("/projects/:projectId/broadcasts/:broadcastId/cancel").returns<
    Wire<typeof broadcastObject>
  >(),
  testBroadcast: post("/projects/:projectId/broadcasts/:broadcastId/test")
    .takes<WireInput<typeof broadcastTestSendRequest>>()
    .returns<Wire<typeof broadcastIdResponse>>(),
  broadcastRecipients: get("/projects/:projectId/broadcasts/:broadcastId/recipients").returns<
    Wire<typeof broadcastRecipientList>
  >(),

  // Automations (B9). The console's automation carries what stops it from being published.
  automations: get("/projects/:projectId/automations").returns<Wire<typeof automationList>>(),
  automation: get("/projects/:projectId/automations/:automationId").returns<
    Wire<typeof consoleAutomationObject>
  >(),
  createAutomation: post("/projects/:projectId/automations")
    .takes<WireInput<typeof createAutomationRequest>>()
    .returns<Wire<typeof consoleAutomationObject>>(),
  updateAutomation: patch("/projects/:projectId/automations/:automationId")
    .takes<WireInput<typeof updateAutomationRequest>>()
    .returns<Wire<typeof consoleAutomationObject>>(),
  publishAutomation: post("/projects/:projectId/automations/:automationId/publish").returns<
    Wire<typeof consoleAutomationObject>
  >(),
  stopAutomation: post("/projects/:projectId/automations/:automationId/stop").returns<
    Wire<typeof consoleAutomationObject>
  >(),
  automationVersions: get("/projects/:projectId/automations/:automationId/versions").returns<
    Wire<typeof automationVersionList>
  >(),
  automationRuns: get("/projects/:projectId/automations/:automationId/runs").returns<
    Wire<typeof automationRunList>
  >(),
  automationRun: get("/projects/:projectId/automations/:automationId/runs/:runId").returns<
    Wire<typeof automationRunObject>
  >(),

  // Admin portal (B12): the platform's overview, ledgers, support inbox and switches
  adminOverview: get("/admin/overview").returns<Wire<typeof adminOverview>>(),
  adminOrgs: get("/admin/orgs").returns<Wire<typeof adminOrganizationList>>(),
  adminOrg: get("/admin/orgs/:orgId").returns<Wire<typeof adminOrganizationDetail>>(),
  updateAdminOrg: patch("/admin/orgs/:orgId")
    .takes<WireInput<typeof updateAdminOrganizationRequest>>()
    .returns<Wire<typeof adminOrganizationDetail>>(),
  adminUsers: get("/admin/users").returns<Wire<typeof adminUserList>>(),
  adminUser: get("/admin/users/:userId").returns<Wire<typeof adminUserDetail>>(),
  updateAdminUser: patch("/admin/users/:userId")
    .takes<WireInput<typeof updateAdminUserRequest>>()
    .returns<Wire<typeof adminUserDetail>>(),
  revokeAdminUserSessions: del("/admin/users/:userId/sessions").returns<
    Wire<typeof adminUserSessionsRevoked>
  >(),
  adminHealth: get("/admin/health").returns<Wire<typeof adminHealthList>>(),
  adminUsage: get("/admin/usage").returns<Wire<typeof adminUsage>>(),
  adminAudit: get("/admin/audit").returns<Wire<typeof adminAuditList>>(),
  adminIntegrations: get("/admin/integrations").returns<Wire<typeof adminIntegrations>>(),
  adminSupport: get("/admin/support").returns<Wire<typeof supportTicketList>>(),
  adminTicket: get("/admin/support/:ticketId").returns<Wire<typeof supportTicketDetail>>(),
  replyAdminTicket: post("/admin/support/:ticketId/replies")
    .takes<WireInput<typeof replySupportTicketRequest>>()
    .returns<Wire<typeof supportTicketDetail>>(),
  updateAdminTicket: patch("/admin/support/:ticketId")
    .takes<WireInput<typeof updateSupportTicketRequest>>()
    .returns<Wire<typeof supportTicketDetail>>(),
  adminSettings: get("/admin/settings").returns<Wire<typeof platformSettingsObject>>(),
  updateAdminSettings: patch("/admin/settings")
    .takes<WireInput<typeof updatePlatformSettingsRequest>>()
    .returns<Wire<typeof platformSettingsObject>>(),
} as const satisfies Record<string, AnyEndpoint>;

/**
 * `GET /projects/:projectId/emails/:emailId`: the native email object, with download links on
 * its attachments, the timeline oldest first, and the webhook deliveries of its events (core's
 * `consoleEmailDetail`, which the API builds it with in apps/api/src/routes/console/emails.ts).
 */
export type EmailDetail = Wire<typeof consoleEmailDetail>;

/** A console test send: the native send request, live (default) or test mode. */
export type TestEmailRequest = WireInput<typeof sendEmailRequest> &
  WireInput<typeof consoleTestEmailOptions>;

export type Endpoints = typeof endpoints;
/** The success body of an endpoint: `ResponseOf<typeof endpoints.emails>`. */
export type ResponseOf<E> = E extends Endpoint<Method, string, infer Res, unknown> ? Res : never;
/** The JSON body an endpoint takes, `undefined` for none. */
export type BodyOf<E> = E extends Endpoint<Method, string, unknown, infer Body> ? Body : never;

type ParamName<P extends string> = P extends `${string}:${infer Name}/${infer Rest}`
  ? Name | ParamName<`/${Rest}`>
  : P extends `${string}:${infer Name}`
    ? Name
    : never;

/** The `:params` a path pattern needs, as an object: `{ projectId: string; emailId: string }`. */
export type PathParams<P extends string> = { readonly [K in ParamName<P>]: string };

/** Fills a pattern's `:params` (URI-encoded); a missing one is a programming error. */
export function fill<P extends string>(pattern: P, params: PathParams<P>): string {
  const values: Readonly<Record<string, string | undefined>> = params;
  return pattern.replace(/:([A-Za-z]+)/g, (_match, name: string) => {
    const value = values[name];
    if (value === undefined || value === "") {
      throw new Error(`Missing path parameter ${name} for ${pattern}`);
    }
    return encodeURIComponent(value);
  });
}
